import { Sensor, Study, StudyFull } from '../models';
import {
  ApiEndpoints,
  Endpoint,
  Study as StudyEntity,
  StudyDownloadPackageCreateRequest,
  StudyFull as StudyFullEntity,
  SubjectDownloadPackageCreateRequest,
} from 'adc-sirius-module-common';
import { urls } from '../utilities/dataHelpers';
import {
  mapSensorEntityToSensor,
  mapSiteEntityToSite,
  mapStudyEntityToStudy,
  mapStudyToStudyEntity,
  mapSubjectEntityToSubject,
} from '../utilities/mappers';
import mediator, { MediatorRequestWithResponse } from '../utilities/mediator';
import { DOWNLOAD_CONFIG, DOWNLOAD_JOB_CONFIG, JobStatus } from '../constants';
import i18n from '../utilities/i18n';

const handleGetStudiesByOrganization = (
  request: MediatorRequestWithResponse<Study[]>,
) => {
  const state = request.state;

  request.response = new Promise((resolve, reject) => {
    mediator
      .request<StudyEntity[]>('api:invoke', {
        method: 'GET',
        url: `${urls.api}studies/organization/${request.organizationKey}`,
        queryString: state ? `?row_state=${state}` : '',
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(
            (result.data ?? []).map((studyEntity) =>
              mapStudyEntityToStudy(studyEntity),
            ),
          );
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error fetching studies:', err);
        reject(err);
      });
  });
};

const handleGetStudyByKey = (request: MediatorRequestWithResponse<Study>) => {
  request.response = new Promise((resolve, reject) => {
    const studyKey = request.key;

    mediator
      .request<StudyEntity>('api:invoke', {
        method: 'GET',
        url: `${urls.api}studies/${studyKey}`,
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(mapStudyEntityToStudy(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error fetching study by ID:', err);
        reject(err);
      });
  });
};

const handleCreateStudy = (
  request: MediatorRequestWithResponse<Partial<Study>>,
) => {
  request.response = new Promise((resolve, reject) => {
    mediator
      .request<StudyEntity>('api:invoke', {
        method: 'POST',
        url: urls.api + 'studies',
        data: mapStudyToStudyEntity(request.data),
      })
      .then((result) => {
        if (result.status === 201) {
          resolve(mapStudyEntityToStudy(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error creating study:', err);
        reject(err);
      });
  });
};

const handleUpdateStudy = (
  request: MediatorRequestWithResponse<Partial<Study>>,
) => {
  request.response = new Promise((resolve, reject) => {
    mediator
      .request<StudyEntity>('api:invoke', {
        method: 'PUT',
        url: `${urls.api}studies`,
        data: mapStudyToStudyEntity(request.data),
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(mapStudyEntityToStudy(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error updating study:', err);
        reject(err);
      });
  });
};

const handleGetStudyFullByKey = (
  request: MediatorRequestWithResponse<StudyFull>,
) => {
  request.response = new Promise((resolve, reject) => {
    const studyKey = request.key;

    mediator
      .request<StudyFullEntity>('api:invoke', {
        method: 'GET',
        url: `${urls.api}studies/${studyKey}/full`,
      })
      .then((result) => {
        if (result.status === 200) {
          const subjects = result.data.subjects ?? [];
          const sites = result.data.sites ?? [];
          const sensorsBySubject = result.data.sensors ?? {};
          const sensors = new Map<string, Sensor[]>();
          const conditions = new Set<string>();

          subjects.forEach(({ key }) => {
            const sensorsForSubject = sensorsBySubject[key] || [];

            sensors.set(key, sensorsForSubject.map(mapSensorEntityToSensor));
            sensorsForSubject.forEach((sensor) => {
              conditions.add(sensor.ClinicalSensorDetails.conditionCode);
            });
          });

          resolve({
            study: mapStudyEntityToStudy(result.data.study),
            sites: sites.map(mapSiteEntityToSite),
            subjects: subjects.map(mapSubjectEntityToSubject),
            sensors,
            conditions: Array.from(conditions),
          });
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error fetching full study by key:', err);
        reject(err);
      });
  });
};

type DownloadDataOptions = {
  createJobEndpoint: Endpoint;
  jobStatusEndpoint: Endpoint;
  downloadEndpoint: Endpoint;
  createJobPayload:
    | StudyDownloadPackageCreateRequest
    | SubjectDownloadPackageCreateRequest;
  onProgress?: (progressPercent: number) => void;
};

export type DownloadPackageResult = {
  blob: Blob;
  checksumSha256?: string;
};

type DownloadErrorResponse = {
  status?: number;
};

const createDownloadError = (response?: DownloadErrorResponse) => {
  switch (response?.status) {
    case 400:
      return new Error(i18n.t('Modal_Download.error_invalid_request'));
    case 404:
      return new Error(i18n.t('Modal_Download.error_not_found'));
    case 409:
      return new Error(i18n.t('Modal_Download.error_conflict'));
    case 429:
      return new Error(i18n.t('Modal_Download.error_rate_limit'));
    case 500:
      return new Error(i18n.t('Modal_Download.error_server'));
    default:
      return new Error(i18n.t('Modal_Download.error_generic_failed'));
  }
};

const handleDownloadData = (options: DownloadDataOptions) => {
  const {
    createJobEndpoint,
    jobStatusEndpoint,
    downloadEndpoint,
    createJobPayload,
    onProgress,
  } = options;

  return new Promise<DownloadPackageResult>((resolve, reject) => {
    const processDownload = async () => {
      try {
        // Step 1: Create download job
        const jobResponse = await mediator.request<{
          jobKey: string;
          status: JobStatus;
          queuedAtUtc: number;
        }>('api:invoke', {
          method: createJobEndpoint.Method,
          url: urls.api + createJobEndpoint.Path,
          data: createJobPayload,
        });

        if (jobResponse.status !== 202) {
          console.error('Error creating download job:', jobResponse.status);
          throw createDownloadError(jobResponse);
        }

        const { jobKey } = jobResponse.data;

        // Step 2: Poll job status until complete
        const pollStatus = async (): Promise<void> => {
          const statusResponse = await mediator.request<{
            jobKey: string;
            status: JobStatus;
            progressPercent: number;
            failureReason?: string;
            completedAtUtc?: number;
            checksumSha256?: string;
          }>('api:invoke', {
            method: jobStatusEndpoint.Method,
            url: urls.api + jobStatusEndpoint.Path.replace(':jobKey', jobKey),
          });

          if (statusResponse.status !== 200) {
            console.error('Error checking job status:', statusResponse.status);
            throw createDownloadError(statusResponse);
          }

          const { status, progressPercent } = statusResponse.data;

          // Notify progress if callback provided
          if (onProgress) {
            onProgress(progressPercent || 0);
          }

          if (status === JobStatus.COMPLETE) {
            // Step 3: Download the actual file once job is complete
            const downloadResult = await mediator.request<Blob>('api:invoke', {
              method: downloadEndpoint.Method,
              url: urls.api + downloadEndpoint.Path.replace(':jobKey', jobKey),
              responseType: 'blob',
            });

            // Success: API returns blob directly
            if (downloadResult instanceof Blob) {
              resolve({
                blob: downloadResult,
                checksumSha256: statusResponse.data.checksumSha256,
              });
            } else {
              console.error(
                'Error downloading file:',
                downloadResult?.['status'],
              );
              throw createDownloadError(
                downloadResult as DownloadErrorResponse,
              );
            }
          } else if (status === JobStatus.FAILED) {
            console.error(
              'Download job failed:',
              statusResponse.data.failureReason,
            );
            throw new Error(i18n.t('Modal_Download.error_generic_failed'));
          } else {
            // Job still in progress (queued, processing, or other status)
            setTimeout(() => {
              pollStatus().catch(reject);
            }, DOWNLOAD_JOB_CONFIG.POLL_INTERVAL_MS);
          }
        };

        // Start polling
        await pollStatus();
      } catch (error) {
        console.error('Error downloading study data:', error);

        if (error instanceof Error) {
          reject(error);
          return;
        }

        reject(new Error(i18n.t('Modal_Download.error_generic_failed')));
      }
    };

    // Start the async process
    processDownload();
  });
};

const handleDownloadSelectedSubjectData = (
  request: MediatorRequestWithResponse<DownloadPackageResult>,
) => {
  const { subjectKeys } = request;

  if (!subjectKeys || subjectKeys.length === 0) {
    request.response = Promise.reject(
      new Error(i18n.t('Modal_Download.error_no_subjects')),
    );
    return;
  }

  request.response = handleDownloadData({
    createJobEndpoint: ApiEndpoints.EP_SUBJECT_CREATE_DOWNLOAD_JOB,
    jobStatusEndpoint: ApiEndpoints.EP_SUBJECT_GET_DOWNLOAD_JOB_STATUS,
    downloadEndpoint: ApiEndpoints.EP_SUBJECT_GET_DOWNLOAD_JOB,
    createJobPayload: {
      subjectKeys,
      outputFormat: DOWNLOAD_CONFIG.OUTPUT_FORMAT,
    },
    onProgress: request.onProgress,
  });
};

const handleDownloadStudyData = (
  request: MediatorRequestWithResponse<DownloadPackageResult>,
) => {
  request.response = handleDownloadData({
    createJobEndpoint: ApiEndpoints.EP_STUDY_CREATE_DOWNLOAD_JOB,
    jobStatusEndpoint: ApiEndpoints.EP_STUDY_GET_DOWNLOAD_JOB_STATUS,
    downloadEndpoint: ApiEndpoints.EP_STUDY_GET_DOWNLOAD_JOB,
    createJobPayload: {
      studyKey: request.studyKey,
      outputFormat: DOWNLOAD_CONFIG.OUTPUT_FORMAT,
    },
    onProgress: request.onProgress,
  });
};

mediator.subscribe('study:getStudies', handleGetStudiesByOrganization);
mediator.subscribe('study:getStudyByKey', handleGetStudyByKey);
mediator.subscribe('study:createStudy', handleCreateStudy);
mediator.subscribe('study:updateStudy', handleUpdateStudy);
mediator.subscribe('study:getStudyFullByKey', handleGetStudyFullByKey);
mediator.subscribe(
  'study:downloadSelectedSubjectData',
  handleDownloadSelectedSubjectData,
);
mediator.subscribe('study:downloadStudyData', handleDownloadStudyData);
